import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Textarea';

interface ActivityNoteFocusEditorProps {
  /** The note's label ("File note" or "Note"): names the dialog and the field. */
  title: string;
  /** The activity's description, so a long note stays tied to the entry it backs up. */
  description: string;
  textareaId: string;
  value: string;
  onChange: (value: string) => void;
  /** When false the text can be read and selected but not changed. */
  editable: boolean;
  placeholder?: string;
  /** Hint and counter, shared with the inline field. */
  footnote: ReactNode;
  describedBy?: string;
  onClose: () => void;
}

/**
 * Full-screen editor for long notes such as meeting minutes. It edits the same
 * form value as the inline field, so "Done" only closes it.
 *
 * Portalled to the body and above the activity dialog (z-50), because it opens
 * from inside one.
 */
export function ActivityNoteFocusEditor({
  title,
  description,
  textareaId,
  value,
  onChange,
  editable,
  placeholder,
  footnote,
  describedBy,
  onClose,
}: ActivityNoteFocusEditorProps) {
  const titleId = useId();
  const trapRef = useFocusTrap(true, { autoFocus: false });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, []);

  // The activity dialog also closes on Escape, from a bubble-phase listener on
  // the document. Handling the key in the capture phase and stopping it there
  // closes only this editor.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    }
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);

  return createPortal(
    <div
      ref={trapRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="animate-fade-in fixed inset-0 z-[60] flex flex-col bg-bg-primary"
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <h2 id={titleId} className="font-serif text-lg font-semibold text-secondary">
            {title}
          </h2>
          {description && <p className="mt-0.5 truncate text-sm text-text-muted">{description}</p>}
        </div>
        <Button type="button" size="sm" onClick={onClose}>
          Done
        </Button>
      </div>

      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col gap-2 px-4 py-4 sm:px-6">
        <div className="flex min-h-0 flex-1 flex-col [&>div]:min-h-0 [&>div]:flex-1">
          <Textarea
            ref={textareaRef}
            id={textareaId}
            aria-label={title}
            aria-describedby={describedBy}
            placeholder={placeholder}
            className="min-h-0 flex-1"
            value={value}
            readOnly={!editable}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
        {footnote}
      </div>
    </div>,
    document.body,
  );
}
